/**
 * 通用请求（Web 端：axios + localStorage）
 */
import axios from 'axios'
import { API_BASE_URL } from '@/config'

const instance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
})

instance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  return config
})

/** 将 FastAPI 的 detail（字符串或校验错误数组）规范为 { detail: string }，便于页面 Toast */
function normalizeErrorPayload(data) {
  if (data == null) return null
  if (typeof data === 'string') {
    try {
      return normalizeErrorPayload(JSON.parse(data))
    } catch {
      return { detail: data }
    }
  }
  if (typeof data !== 'object') return { detail: String(data) }
  if (typeof data.detail === 'string') return { detail: data.detail }
  if (Array.isArray(data.detail)) {
    const parts = data.detail
      .map((i) => (i && i.msg != null ? String(i.msg) : ''))
      .filter(Boolean)
    return { detail: parts.length ? parts.join('；') : '请求参数错误' }
  }
  return data
}

instance.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    const res = error.response
    // 跳转登录页时使用当前 pathname 作为 base，保证在部署路径（如 /short_video/pc/）下正确跳转
    const loginUrl = window.location.pathname.replace(/\/?$/, '') + '#/login'
    if (res && res.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      if (!window.location.pathname.includes('login')) {
        window.location.href = loginUrl
      }
      return Promise.reject(new Error('Unauthorized'))
    }
    if (res && res.status === 403) {
      const rawDetail = res.data?.detail
      const detailStr =
        typeof rawDetail === 'string'
          ? rawDetail
          : Array.isArray(rawDetail)
            ? JSON.stringify(rawDetail)
            : ''
      const authKeywords = ['Not authenticated', 'NotAuthenticated', 'authentication', '未认证', '认证']
      if (authKeywords.some((k) => detailStr.toLowerCase().includes(k.toLowerCase()))) {
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        if (!window.location.pathname.includes('login')) {
          window.location.href = loginUrl
        }
        return Promise.reject(new Error('Unauthorized'))
      }
    }
    const normalized = res?.data != null ? normalizeErrorPayload(res.data) : null
    const payload =
      normalized ?? { detail: error.message || '网络异常，请稍后重试' }

    if (!error.config?.silent && payload?.detail) {
      console.warn('[API]', payload.detail)
    }
    return Promise.reject(payload)
  }
)

export function get(url, params, options = {}) {
  return instance.get(url, { params, ...options })
}

export function post(url, data, options = {}) {
  return instance.post(url, data, options)
}

export function put(url, data, options = {}) {
  return instance.put(url, data, options)
}

export function del(url, data, options = {}) {
  return instance.delete(url, { data, ...options })
}

export default instance
